import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Button, Text } from 'rsuite';
import FormTextField from '../form/FormTextField';
import SchemaTyped from 'rsuite/esm/Schema/Schema';
import { useDispatch, useSelector } from 'react-redux';
import {
  retrieveCognitoUserAsync,
  terminateSession,
} from '../../store/slices/auth/authThunks';
import {
  logout,
  resetWorkProgressState,
} from '../../store/slices/auth/authSlice';
import { isNullUndefinedOrEmptyString } from '../../common/utils';
import {
  selectIsAuthCodeRequired,
  selectWorkErrorMessage,
  selectWorkFailed,
  selectWorkProgressMessage,
  selectWorkStarted,
  selectWorkSuccess,
} from '../../store/slices/auth/authSelectors';
import IconIndicator from '../user-feedback/IconIndicator';
import Notice from '../user-feedback/Notice';
import AuthCodeModal from './AuthCodeModal';
import usePlaceNavigate from '../../hooks/usePlaceNavigate';
import { selectConfigExpressRegistrationVerbiage } from '../../store/slices/config/configSelectors';
import pluralize from 'pluralize';
import { clearPersistedState } from '../../common/tokenPersistence';

const LoginWidget = ({
  errorMessage,
  buttonText,
  showHeader = true,
  headerText,
  showDescription = true,
}) => {
  const form = useRef();
  const placeNavigate = usePlaceNavigate();
  const dispatch = useDispatch();

  const [isLoginError, setIsLoginError] = useState(false);
  const [localLoginErrorMsg, setLocalLoginErrorMsg] = useState('');
  const [formValues, setFormValues] = useState({});
  const [formError, setFormError] = useState({});

  const loginStarted = useSelector(selectWorkStarted);
  const loginSuccess = useSelector(selectWorkSuccess);
  const loginFailed = useSelector(selectWorkFailed);
  const loginProgressMsg = useSelector(selectWorkProgressMessage);
  const loginErrorMsg = useSelector(selectWorkErrorMessage);
  const showAuthModal = useSelector(selectIsAuthCodeRequired);
  const licenseVerbiage = useSelector(selectConfigExpressRegistrationVerbiage);

  const formModel = useMemo(() => {
    return {
      email: '',
    };
  }, []);

  const { StringType } = SchemaTyped.Types;

  const validationModel = {
    email: StringType().isEmail().isRequired(),
  };

  useEffect(() => {
    setFormValues(formModel);
  }, [formModel, setFormValues]);

  useEffect(() => {
    setIsLoginError(loginFailed);
    setLocalLoginErrorMsg(
      isNullUndefinedOrEmptyString(loginErrorMsg)
        ? 'Unknown error occurred'
        : loginErrorMsg
    );
  }, [loginErrorMsg, loginFailed]);

  useEffect(() => {
    if (loginSuccess === true) {
      placeNavigate('/dashboard');
      dispatch(resetWorkProgressState());
    }
  }, [loginSuccess, dispatch, placeNavigate]);

  const onLoginSubmit = (e) => {
    e.preventDefault();
    if (!form.current.check()) return;
    dispatch(retrieveCognitoUserAsync(formValues['email']));
  };

  const onLoginModalReject = () => {
    dispatch(logout());
    clearPersistedState();
    dispatch(terminateSession());
  };

  return (
    <>
      <div>
        {showHeader && <Text className="header-text-h3">{headerText}</Text>}
        {showDescription && (
          <Text className="paragraph">
            To get started, please enter the email address associated with your{' '}
            {pluralize(licenseVerbiage)} below.
          </Text>
        )}
        <Form
          className="mt-8"
          ref={form}
          checkTrigger="none"
          formDefaultValue={formModel}
          onCheck={setFormError}
          onChange={setFormValues}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          fluid
        >
          <div>
            <FormTextField
              name="email"
              label="Email Address"
              validationRule={validationModel.email}
              hasError={formError['email']}
              errorMessage={errorMessage ?? 'Email address is required'}
              inputFieldType="email"
            />

            <Button
              type="submit"
              onClick={onLoginSubmit}
              className="btn-primary mt-16 ph-48 sm-100-percent"
            >
              {buttonText}
            </Button>
          </div>
        </Form>
      </div>
      <div className="mt-24">
        {loginStarted === true && (
          <IconIndicator
            className="mb-24"
            text={loginProgressMsg}
          />
        )}
        {isLoginError === true && (
          <Notice
            className="mb-24"
            type="warning"
            message={localLoginErrorMsg}
            iconSize={16}
            messageStyle={{ fontSize: 12 }}
          />
        )}
      </div>
      <AuthCodeModal
        showModal={showAuthModal}
        onModalReject={onLoginModalReject}
      />
    </>
  );
};

export default LoginWidget;
